import { useState } from "react";
import { SupertallsTable } from "./components/SupertallsTable";
import { BuildingMap, WalkingTourStats } from "./components/BuildingMap";
import { WalkingRoutes } from "./components/WalkingRoutes";
import { SupertallsChecklist } from "./components/SupertallsChecklist";
import { buildings as allBuildings } from "./data/buildings";
import type { Building } from "./types/Building";
import "./index.css";

export function App() {
  const [selectedBuilding, setSelectedBuilding] = useState<Building | null>(null);
  const [currentView, setCurrentView] = useState<'map' | 'table' | 'checklist'>('map');
  
  // Filter to only show true supertalls (300m+ / 984ft+)
  const supertalls = allBuildings.filter(building => building.height_meters >= 300);

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            🏗️ NYC Supertalls Walking Tour
          </h1>
          <p className="text-lg text-gray-600">
            Explore Manhattan's {supertalls.length} true supertalls (300m+ / 984ft+) on an interactive map
          </p>
          <div className="mt-3 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">
              📏 <strong>Supertall Definition:</strong> Buildings 300m (984ft) or taller. 
              Filtered from {allBuildings.length} total buildings to show only {supertalls.length} true supertalls.
            </p>
          </div>
        </div>

        {/* View Toggle */}
        <div className="mb-6">
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
            <button
              onClick={() => setCurrentView('map')}
              className={`px-4 py-2 rounded-md font-medium transition-colors ${
                currentView === 'map'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              🗺️ Map View
            </button>
            <button
              onClick={() => setCurrentView('checklist')}
              className={`px-4 py-2 rounded-md font-medium transition-colors ${
                currentView === 'checklist'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              ✅ Checklist
            </button>
            <button
              onClick={() => setCurrentView('table')}
              className={`px-4 py-2 rounded-md font-medium transition-colors ${
                currentView === 'table'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              📊 Table View
            </button>
          </div>
        </div>

        {currentView === 'map' && (
          <>
            {/* Walking Tour Routes */}
            <WalkingRoutes buildings={supertalls} />
            
            {/* Walking Tour Statistics */}
            <WalkingTourStats buildings={supertalls} />
            
            {/* Interactive Map */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Interactive Supertalls Map
              </h2>
              <p className="text-gray-600 mb-4">
                Showing only true supertalls (300m+ / 984ft+). Click any marker to see details!
              </p>
              <BuildingMap
                buildings={supertalls}
                selectedBuilding={selectedBuilding}
                onBuildingSelect={setSelectedBuilding}
              />
            </div>
          </>
        )}

        {currentView === 'checklist' && (
          <SupertallsChecklist buildings={supertalls} />
        )}

        {currentView === 'table' && (
          <SupertallsTable />
        )}
      </div>
    </div>
  );
}

export default App;
